import * as React from 'react'
import { Button } from '@/ui/button'
import { Loader } from '@/ui'
import { TextAreaField } from '@/ui/form/controls/TextAreaField'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useUser } from '@/modules/shared/context/UserContext'
import {
  useMutationCompanyIpAllowList,
  useQueryCompanyIpAllowList,
} from '@/modules/shared/query-client/useCompanyIpAllowList'

const toLines = (text: string) =>
  text
    .split(/[\n,;]/)
    .map((s) => s.trim())
    .filter(Boolean)

const sameList = (a: string[], b: string[]) =>
  a.length === b.length && a.every((v, i) => v === b[i])

const ipv4ToInt = (ip: string): number | null => {
  const parts = ip.split('.')
  if (parts.length !== 4) return null
  const nums = parts.map(Number)
  if (nums.some((n) => !Number.isInteger(n) || n < 0 || n > 255)) return null
  return ((nums[0] << 24) | (nums[1] << 16) | (nums[2] << 8) | nums[3]) >>> 0
}

// Tylko podpowiedź w UI (IPv4); serwer i tak sprawdza dokładnie, także IPv6.
const rangeContains = (range: string, ip: string) => {
  if (range === ip) return true
  const [network, prefixText] = range.split('/')
  const prefix = prefixText === undefined ? 32 : Number(prefixText)
  const net = ipv4ToInt(network)
  const addr = ipv4ToInt(ip)
  if (net === null || addr === null || !Number.isInteger(prefix) || prefix < 0 || prefix > 32)
    return false
  const mask = prefix === 0 ? 0 : (~0 << (32 - prefix)) >>> 0
  return ((net & mask) >>> 0) === ((addr & mask) >>> 0)
}

// Lista dozwolonych adresów IP firmy — wspólna dla panelu admina i panelu konta usługowego.
export const IpAllowListEditor: React.FC<{ companyId: string }> = ({ companyId }) => {
  const { t } = useLocalize()
  const { isAdmin } = useUser()
  const query = useQueryCompanyIpAllowList(companyId)
  const mutation = useMutationCompanyIpAllowList(companyId)
  const [text, setText] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const [saved, setSaved] = React.useState(false)

  const savedRanges = React.useMemo(() => query.data?.ranges ?? [], [query.data])
  React.useEffect(() => {
    setText(savedRanges.join('\n'))
  }, [savedRanges])

  if (query.isLoading) return <Loader />
  if (query.error || !query.data) {
    const status = (query.error as any)?.response?.status
    return (
      <div className="tw-text-sm tw-text-red-600">
        {status === 403 ? t('ipAllowList.no-permission') : t('ipAllowList.load-error')}
      </div>
    )
  }

  const data = query.data
  const lines = toLines(text)
  const dirty = !sameList(lines, savedRanges)
  const currentIp = data.currentIp
  const currentListed = !!currentIp && lines.some((r) => rangeContains(r, currentIp))

  const errorMessage = (e: unknown) => {
    const body = (e as any)?.response?.data
    switch (body?.code) {
      case 'IP_ALLOW_LIST_INVALID':
        return t('ipAllowList.error-invalid', { entries: body.message ?? '' })
      case 'IP_ALLOW_LIST_TOO_MANY':
        return t('ipAllowList.error-too-many', { max: body.message ?? '' })
      case 'IP_ALLOW_LIST_EXCLUDES_CURRENT_IP':
        return t('ipAllowList.error-excludes-current', { ip: body.message ?? '' })
      default:
        return t('ipAllowList.error-save')
    }
  }

  const save = () => {
    setError(null)
    setSaved(false)
    mutation.mutate(
      { ranges: lines },
      {
        onSuccess: () => setSaved(true),
        onError: (e) => setError(errorMessage(e)),
      },
    )
  }

  return (
    <div className="tw-flex tw-max-w-[640px] tw-flex-col tw-gap-4">
      <div>
        <h3 className="tw-mb-1 tw-text-base tw-font-semibold tw-text-content-base">
          {t('ipAllowList.title')}
        </h3>
        <p className="tw-text-sm tw-text-content-secondary">
          {t(
            data.isServiceCompany
              ? 'ipAllowList.description-service-account'
              : 'ipAllowList.description',
          )}
        </p>
      </div>

      {data.serviceAccountName && data.serviceAccountRanges.length > 0 && (
        <div className="tw-rounded tw-border tw-border-base-300 tw-bg-base-200 tw-px-3 tw-py-2 tw-text-sm">
          <div className="tw-mb-1 tw-text-content-secondary">
            {t('ipAllowList.inherited', { name: data.serviceAccountName })}
          </div>
          <div className="tw-font-mono tw-text-xs">{data.serviceAccountRanges.join(', ')}</div>
        </div>
      )}

      <TextAreaField
        fullWidth
        rows={8}
        label={t('ipAllowList.ranges-label')}
        helper={t('ipAllowList.ranges-helper')}
        placeholder={'203.0.113.10\n203.0.113.0/24'}
        className="tw-font-mono !tw-bg-gray-50 focus:!tw-bg-white"
        value={text}
        onChange={(e) => {
          setText(e.target.value)
          setSaved(false)
        }}
      />

      {currentIp && (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-text-sm">
          <span className="tw-text-content-secondary">
            {t('ipAllowList.current-ip')}{' '}
            <span className="tw-font-mono tw-text-content-base">{currentIp}</span>
          </span>
          {!currentListed && (
            <Button
              type="button"
              variant="primary"
              mode="outline"
              size="sm"
              onClick={() => setText(lines.concat(currentIp).join('\n'))}
            >
              {t('ipAllowList.add-current-ip')}
            </Button>
          )}
        </div>
      )}

      {lines.length > 0 && !currentListed && (
        <div className="tw-rounded tw-bg-yellow-50 tw-px-3 tw-py-2 tw-text-sm tw-text-yellow-800">
          {t(isAdmin() ? 'ipAllowList.warning-admin' : 'ipAllowList.warning-owner')}
        </div>
      )}

      {error && (
        <div className="tw-rounded tw-bg-red-100 tw-px-3 tw-py-2 tw-text-sm tw-text-red-800">
          {error}
        </div>
      )}
      {saved && !dirty && (
        <div className="tw-rounded tw-bg-green-50 tw-px-3 tw-py-2 tw-text-sm tw-text-green-800">
          {t(savedRanges.length > 0 ? 'ipAllowList.saved' : 'ipAllowList.saved-empty')}
        </div>
      )}

      <div className="tw-flex tw-gap-2">
        <Button
          type="button"
          variant="primary"
          size="sm"
          disabled={!dirty}
          loading={mutation.isPending}
          onClick={save}
        >
          {t('ipAllowList.save')}
        </Button>
        <Button
          type="button"
          variant="neutral"
          mode="outline"
          size="sm"
          disabled={!dirty || mutation.isPending}
          onClick={() => {
            setText(savedRanges.join('\n'))
            setError(null)
          }}
        >
          {t('ipAllowList.cancel')}
        </Button>
      </div>
    </div>
  )
}
